import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Target, Users, CheckCircle, Clock, UserPlus, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { API_URL } from '../../lib/api-client';

interface Lead {
  _id: string;
  userId?: string; // For assigned leads, this is the user ID
  name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  country: string;
  registeredDate: string;
  assignedDate?: string;
  expiryDate?: string;
  daysRemaining?: number;
  assignedTo: string | null;
  assignedToName: string | null;
  status: 'unassigned' | 'active' | 'contacted' | 'interested' | 'not-interested' | 'expired' | 'converted';
  lastContactDate?: string;
  notes?: string;
  plan: string;
}

interface TeamMember {
  _id: string;
  firstName: string;
  lastName: string;
  marketingData: {
    isActive: boolean;
    totalLeadsAssigned: number;
  };
}

interface LeadManagementProps {
  onUpdate: () => void;
}

export default function LeadManagement({ onUpdate }: LeadManagementProps) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    fetchLeads();
    fetchTeamMembers();
  }, []);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/marketing-manager/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(data.leads || []);
    } catch (error: any) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/marketing-manager/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMembers(data.teamMembers || []);
    } catch (error: any) {
      console.error('Error fetching team members:', error);
    }
  };

  const handleAssignLead = async () => {
    if (!selectedLead || !selectedAssignee) {
      toast.error('Please select a team member');
      return;
    }

    try {
      setAssigning(true);
      const token = localStorage.getItem('token');
      
      // Use userId if the lead is already assigned, otherwise use _id (for unassigned users)
      const leadId = selectedLead.userId || selectedLead._id;
      
      await axios.post(
        `${API_URL}/marketing-manager/leads/${leadId}/assign`,
        { marketingUserId: selectedAssignee },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Lead assigned successfully');
      setShowAssignDialog(false);
      setSelectedLead(null);
      setSelectedAssignee('');
      fetchLeads();
      onUpdate();
    } catch (error: any) {
      console.error('Error assigning lead:', error);
      toast.error(error.response?.data?.message || 'Failed to assign lead');
    } finally {
      setAssigning(false);
    }
  };

  const openAssignDialog = (lead: Lead) => {
    setSelectedLead(lead);
    setSelectedAssignee(lead.assignedTo || '');
    setShowAssignDialog(true);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      unassigned: { label: 'Unassigned', variant: 'secondary' },
      active: { label: 'Active', variant: 'outline' },
      contacted: { label: 'Contacted', variant: 'outline' },
      interested: { label: 'Interested', variant: 'default' },
      'not-interested': { label: 'Not Interested', variant: 'destructive' },
      expired: { label: 'Expired', variant: 'destructive' },
      converted: { label: 'Converted', variant: 'default' }
    };
    
    const config = statusConfig[status] || statusConfig.unassigned;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone.includes(searchTerm);
    
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    
    const matchesAssignment = 
      assignmentFilter === 'all' ||
      (assignmentFilter === 'assigned' && lead.assignedTo) ||
      (assignmentFilter === 'unassigned' && !lead.assignedTo);
    
    return matchesSearch && matchesStatus && matchesAssignment;
  });

  const stats = {
    total: leads.length,
    assigned: leads.filter(l => l.assignedTo).length,
    unassigned: leads.filter(l => !l.assignedTo).length,
    converted: leads.filter(l => l.status === 'converted').length
  };

  if (loading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="p-8">
          <div className="text-center text-gray-500">Loading leads...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Leads</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Target className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Assigned</p>
                  <p className="text-2xl font-bold">{stats.assigned}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Unassigned</p>
                  <p className="text-2xl font-bold">{stats.unassigned}</p>
                </div>
                <Clock className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Converted</p>
                  <p className="text-2xl font-bold">{stats.converted}</p>
                </div>
                <Users className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="shadow-sm">
          <CardHeader className="bg-white dark:bg-gray-800 border-b">
            <CardTitle>All Leads</CardTitle>
            <CardDescription>Assign and manage leads for your team</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex flex-wrap gap-4 mb-6">
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-md"
                icon={<Search className="h-4 w-4" />}
              />

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="interested">Interested</SelectItem>
                  <SelectItem value="not-interested">Not Interested</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                </SelectContent>
              </Select>

              <Select value={assignmentFilter} onValueChange={setAssignmentFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by assignment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Leads</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Leads List */}
            {filteredLeads.length > 0 ? (
              <div className="space-y-3">
                {filteredLeads.map((lead) => (
                  <div
                    key={lead._id}
                    className="flex items-center justify-between p-4 border rounded-lg bg-white dark:bg-gray-800 hover:shadow-md transition-all"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold">
                          {lead.name}
                        </h3>
                        {getStatusBadge(lead.status)}
                        {lead.daysRemaining !== null && lead.daysRemaining !== undefined && (
                          <Badge variant={lead.daysRemaining < 5 ? "destructive" : "secondary"}>
                            {lead.daysRemaining} days left
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mt-1">
                        <span>{lead.email}</span>
                        <span>{lead.phone}</span>
                        <span>{lead.city}, {lead.state}</span>
                        <span>Plan: {lead.plan}</span>
                        {lead.assignedToName && (
                          <Badge variant="outline">
                            Assigned to: {lead.assignedToName}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openAssignDialog(lead)}
                      className="shrink-0"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      {lead.assignedTo ? 'Reassign' : 'Assign'}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No leads found
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {searchTerm || statusFilter !== 'all' || assignmentFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'No leads available at the moment'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Assign Lead Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Lead</DialogTitle>
            <DialogDescription>
              Assign this lead to a team member
            </DialogDescription>
          </DialogHeader>

          {selectedLead && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h3 className="font-semibold mb-2">
                  {selectedLead.name}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{selectedLead.email}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{selectedLead.phone}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{selectedLead.city}, {selectedLead.state}, {selectedLead.country}</p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Select Team Member
                </label>
                <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a team member" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamMembers
                      .filter(m => m.marketingData.isActive)
                      .map((member) => (
                        <SelectItem key={member._id} value={member._id}>
                          {member.firstName} {member.lastName} ({member.marketingData.totalLeadsAssigned} leads)
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAssignDialog(false)}
              disabled={assigning}
            >
              Cancel
            </Button>
            <Button onClick={handleAssignLead} disabled={assigning || !selectedAssignee}>
              {assigning ? 'Assigning...' : 'Assign Lead'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}